import { GraphDateType } from './dataKiGraph';
import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from './graphSlotLayout';
import { DayType, DaysType, GraphData } from './schemas';

/** 推移グラフ（機種比較）で同時表示できるグラフ項目の最大数 */
export const DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS = 5;

/** 機種別推移の上下2グラフで recharts syncId に渡す固定値 */
export const DATA_MODEL_TRANSITION_GRAPH_SYNC_ID = 'modelTransitionGraph';

/** 1本表示時のグラフ高さ（px） */
export const DATA_MODEL_TRANSITION_GRAPH_CHART_HEIGHT = 360;

/**
 * 2本表示時の1本あたりのグラフ高さ（px）。
 * 3本×140px より大きくしつつ、下部固定フッター（100px）上に操作ボタンが収まるサイズ。
 */
export const DATA_MODEL_TRANSITION_GRAPH_COMPACT_CHART_HEIGHT = 200;

/** 3本表示時の1本あたりのグラフ高さ（px）。フルHDで3本が収まるサイズ */
export const DATA_MODEL_TRANSITION_GRAPH_DENSE_CHART_HEIGHT = 140;

/** 4本以上表示時の1本あたりのグラフ高さ（px）。画面からはみ出してもよい */
export const DATA_MODEL_TRANSITION_GRAPH_OVERFLOW_CHART_HEIGHT = 120;

/**
 * 表示本数（スロット数）に応じたグラフ高さ（px）を返す。
 */
export const resolveDataModelTransitionGraphChartHeight = (
  graphCount: number
): number =>
  resolveGraphSlotChartHeight(graphCount, {
    single: DATA_MODEL_TRANSITION_GRAPH_CHART_HEIGHT,
    double: DATA_MODEL_TRANSITION_GRAPH_COMPACT_CHART_HEIGHT,
    triple: DATA_MODEL_TRANSITION_GRAPH_DENSE_CHART_HEIGHT,
    overflow: DATA_MODEL_TRANSITION_GRAPH_OVERFLOW_CHART_HEIGHT,
  });

/** タイトル・機種選択・枠線など、グラフ本体以外の高さ（px） */
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_TITLE_HEIGHT = 48;
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_CONTROLS_HEIGHT = 104;
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_SHARED_LEGEND_HEIGHT = 40;
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_SLOT_GAP = 16;
const DATA_MODEL_TRANSITION_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ（px）を返す。
 * 機種集計と同様、グラフ本数に応じて領域を広げる。
 */
export const resolveDataModelTransitionGraphContainerHeight = (
  slotCount: number,
  options?: { hasMultipleGraphs?: boolean }
): number =>
  resolveGraphSlotContainerHeight(
    slotCount,
    {
      chromeHeight: DATA_MODEL_TRANSITION_GRAPH_CONTAINER_CHROME_HEIGHT,
      titleHeight: DATA_MODEL_TRANSITION_GRAPH_CONTAINER_TITLE_HEIGHT,
      controlsHeight: DATA_MODEL_TRANSITION_GRAPH_CONTAINER_CONTROLS_HEIGHT,
      slotControlsHeight:
        DATA_MODEL_TRANSITION_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
      slotGap: DATA_MODEL_TRANSITION_GRAPH_CONTAINER_SLOT_GAP,
      footerHeight: DATA_MODEL_TRANSITION_GRAPH_CONTAINER_FOOTER_HEIGHT,
      sharedLegendHeight:
        DATA_MODEL_TRANSITION_GRAPH_CONTAINER_SHARED_LEGEND_HEIGHT,
      resolveChartHeight: resolveDataModelTransitionGraphChartHeight,
    },
    options
  );

export type DataModelTransitionGraphFieldItem = {
  field: string;
  data: GraphData;
};

const MODEL_TRANSITION_GRAPH_SERIES_TYPES = [
  'hall',
  'ki',
  'type',
  'kiTag',
] as const;

export type ModelTransitionGraphSeriesType =
  (typeof MODEL_TRANSITION_GRAPH_SERIES_TYPES)[number];

export const isModelTransitionGraphSeriesType = (
  value: unknown
): value is ModelTransitionGraphSeriesType =>
  typeof value === 'string' &&
  MODEL_TRANSITION_GRAPH_SERIES_TYPES.some(
    (seriesType) => seriesType === value
  );

export type DataModelTransitionGraphParams = {
  containsAverage?: boolean;
  seriesType?: ModelTransitionGraphSeriesType;
  /**
   * グラフ API のクエリ param `field`（1項目分）。
   * 複数グラフ表示以前のお気に入りはこちらが保存されている。
   */
  field?: string;
  /**
   * 画面に出すグラフ項目の一覧（最大 DATA_MODEL_TRANSITION_GRAPH_MAX_FIELDS 件）。
   * お気に入り保存用。API リクエストでは使わず、Saga が1件ずつ field に分けて送る。
   */
  fields?: string[];
  dateType?: GraphDateType;
  kiList?: string[];
  tagIds?: string[];
  kiListForSijiritu?: string[];
  sisTypesForSijiritu?: string[];
  shuGroupIds?: string[];
  shuList?: string[];
  areas?: string[];
  halls?: string[];
  makers?: string[];
  sisTypes?: string[];
  startDate?: string;
  endDate?: string;
  excludeToday?: boolean;
  days?: DaysType[];
  dayType?: DayType;
  dateSuffixes?: number[];
  dates?: number[];
};

/** `/data/kiTransition/graph` 1回分のレスポンス（field 1項目あたり） */
export type DataModelTransitionGraphApiResponse = {
  setting: DataModelTransitionGraphParams;
  data: DataModelTransitionGraphFieldItem['data'];
};

/**
 * Redux に保持する検索条件（期間・店舗・機種など）。
 * どのグラフ項目を表示するかは graphList[].field に持ち、setting には含めない（二重管理を避ける）。
 */
export type DataModelTransitionGraphSetting = Omit<
  DataModelTransitionGraphParams,
  'field' | 'fields'
>;

export type DataModelTransitionGraph = {
  setting: DataModelTransitionGraphSetting;
  graphList: DataModelTransitionGraphFieldItem[];
};

/**
 * API レスポンスの setting から Redux 用 setting を作る。
 * field / fields は graphList[].field に一本化するため取り除く（setting と二重管理にしない）。
 * お気に入り保存・再取得時は dataModelTransitionGraphSettingSelector が graphList から再構築する。
 */
export const toDataModelTransitionGraphSetting = (
  params: DataModelTransitionGraphParams
): DataModelTransitionGraphSetting => {
  const { field: _field, fields: _fields, ...setting } = params;
  return setting;
};
